import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";
import { isSameDay } from "date-fns";

import { keyMaker } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import { EnhancedTradeProposal } from "@fartherfinance/frontend/api/Investment/request/getTradeProposal";
import { TradeProposalId } from "@fartherfinance/frontend/api/Investment/types";
import { HookResult } from "@fartherfinance/frontend/api/Types";

// an array of all the dates that the trade proposals were created
type RelatedProposalData = {
  totalBuys: number;
  totalSells: number;
  proposalsLength: number;
  submittedOn: Date[];
};

const countTradesBySide = (
  trades: EnhancedTradeProposal["trades"],
  side: "Buy" | "Sell"
): number => trades.filter((trade) => trade.side === side).length;

const isEnhancedTradeProposal = (
  proposal: any
): proposal is EnhancedTradeProposal => {
  return proposal !== undefined && "proposalId" in proposal;
};

const useCachedRelatedProposals = (
  tradeProposals?: TradeProposalId[],
  submittedAt?: Date
): HookResult<RelatedProposalData> => {
  const tradeProposalQueryKeys = useMemo(
    () => tradeProposals?.map((tpi) => keyMaker(tpi)),
    [tradeProposals]
  );

  const userQueries = useQueries<EnhancedTradeProposal[]>({
    queries: (tradeProposalQueryKeys ?? []).map((queryKey) => {
      return {
        queryKey: queryKey,
      };
    }),
  });

  const tradesFetching = useMemo(
    () => userQueries.some((query) => query.isFetched === false),
    [userQueries]
  );

  const relatedTrades: EnhancedTradeProposal[] | undefined = useMemo(() => {
    // if any of the queries are still fetching, return undefined
    if (tradesFetching === true) {
      return undefined;
    }

    return userQueries
      .map((query) => query.data)
      .filter(isEnhancedTradeProposal);
  }, [tradesFetching, userQueries]);

  const relatedProposalData: RelatedProposalData | undefined = useMemo(() => {
    if (!relatedTrades) {
      return undefined;
    }

    return relatedTrades.reduce<RelatedProposalData>(
      (acc, proposal) => {
        if (!proposal || proposal.submittedAt === null) {
          return acc;
        }

        const shouldCountProposal =
          submittedAt === undefined ||
          isSameDay(proposal.submittedAt, submittedAt);

        if (shouldCountProposal === false) {
          return {
            ...acc,
            submittedOn: [...acc.submittedOn, proposal.submittedAt],
          };
        }

        return {
          proposalsLength: acc.proposalsLength + 1,
          totalBuys: acc.totalBuys + countTradesBySide(proposal.trades, "Buy"),
          totalSells:
            acc.totalSells + countTradesBySide(proposal.trades, "Sell"),
          submittedOn: [...acc.submittedOn, proposal.submittedAt],
        };
      },
      {
        proposalsLength: 0,
        totalBuys: 0,
        totalSells: 0,
        submittedOn: [],
      }
    );
  }, [relatedTrades, submittedAt]);

  return useMemo(() => {
    if (tradesFetching === true) {
      return {
        data: undefined,
        isLoading: true,
        hasError: false,
      };
    }

    if (relatedProposalData === undefined) {
      return {
        data: undefined,
        isLoading: false,
        hasError: true,
        error: new Error("Error calculating related proposals"),
      };
    }

    return {
      data: relatedProposalData,
      isLoading: false,
      hasError: false,
    };
  }, [tradesFetching, relatedProposalData]);
};

export default useCachedRelatedProposals;
